/**
 * Caption-side wrapper for the shared find/replace popover (#2233).
 *
 * Segments = one per caption segment (`{ key: segment.id, text }`).
 * Jump-to-match sets the caption context's current-match cursor;
 * replace-all pushes replaced segments back into the document via
 * `updateCaptionDocument`.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useCaption } from '@utils/providers/caption-context.tsx';
import { useCaptionEditorContext } from '@utils/providers/caption-editor-context.tsx';
import {
  useFindReplace,
  FindReplaceSegment
} from '@components/editors/shared/find-replace/use-find-replace.ts';
import { FindReplacePopover } from '@components/editors/shared/find-replace/find-replace-popover.tsx';

interface Props {
  onClose: () => void;
}

export function CaptionFindReplaceHost({ onClose }: Props) {
  const {
    document: captionDocument,
    updateCaptionDocument,
    setCurrentMatch,
    setSearch: setContextSearch
  } = useCaption();
  const { replaceAllWordsInEditor, replaceOneInEditor } =
    useCaptionEditorContext();

  const segments = useMemo<FindReplaceSegment[]>(() => {
    if (!captionDocument?.document) return [];
    return captionDocument.document
      .filter(seg => typeof seg.text === 'string' && seg.text.length > 0)
      .map(seg => ({ key: String(seg.id), text: seg.text ?? '' }));
  }, [captionDocument?.document]);

  const state = useFindReplace({ segments });

  useEffect(() => {
    setContextSearch(state.query);
  }, [state.query, setContextSearch]);

  const handleJumpToMatch = useCallback(
    (index: number) => {
      const match = state.matches[index];
      if (!match || !captionDocument?.document) return;
      const seg = captionDocument.document.find(
        s => String(s.id) === match.segmentKey
      );
      if (seg) setCurrentMatch([seg]);
    },
    [state.matches, captionDocument?.document, setCurrentMatch]
  );

  useEffect(() => {
    if (state.current) handleJumpToMatch(state.currentIndex);
  }, [state.currentIndex, state.current?.id]);

  const handleReplaceAll = useCallback(() => {
    if (!state.query || !state.replacement || !captionDocument?.document) {
      return;
    }
    // Preserve-first-letter is honoured by applying the hook's own
    // per-match replacement (which handles case) and writing the
    // resulting segments back to the doc. Then sync Slate so mounted
    // rows show the new text.
    const { segments: nextSegments } = state.applyReplaceAll(segments);
    const bySegmentKey = new Map(nextSegments.map(s => [s.key, s.text]));
    const nextDoc = {
      ...captionDocument,
      document: captionDocument.document.map(seg => {
        const nextText = bySegmentKey.get(String(seg.id));
        return nextText === undefined ? seg : { ...seg, text: nextText };
      })
    };
    updateCaptionDocument(nextDoc);
    replaceAllWordsInEditor(
      state.query,
      state.options.caseSensitive,
      state.options.wholeWord,
      state.replacement
    );
  }, [
    state,
    segments,
    captionDocument,
    updateCaptionDocument,
    replaceAllWordsInEditor
  ]);

  // Replace just the current match. The hook's `applyReplaceCurrent`
  // returns a new segment list with ONLY the match at `currentIndex`
  // swapped — we write that back to the caption document AND run a
  // targeted Slate `Transforms.insertText` on the one editor that
  // holds this segment so the mounted row shows the new text (without
  // the Slate side the state updated silently but the editor stayed
  // on its old internal content). Then advance to the next match so
  // the user can walk through confirmations one at a time (the
  // reopen complaint: "even after you find the result you want,
  // Replace all replaces everything").
  const handleReplaceOne = useCallback(() => {
    if (!state.query || !state.replacement || !captionDocument?.document) {
      return;
    }
    const match = state.matches[state.currentIndex];
    if (!match) return;
    const segmentForMatch = captionDocument.document.find(
      s => String(s.id) === match.segmentKey
    );
    if (!segmentForMatch) return;
    const result = state.applyReplaceCurrent(segments);
    if (!result) return;
    const bySegmentKey = new Map(result.segments.map(s => [s.key, s.text]));
    const nextDoc = {
      ...captionDocument,
      document: captionDocument.document.map(seg => {
        const nextText = bySegmentKey.get(String(seg.id));
        return nextText === undefined ? seg : { ...seg, text: nextText };
      })
    };
    updateCaptionDocument(nextDoc);
    replaceOneInEditor(
      segmentForMatch.text ?? '',
      match.from,
      match.to,
      result.replacedText
    );
    state.next();
  }, [
    state,
    segments,
    captionDocument,
    updateCaptionDocument,
    replaceOneInEditor
  ]);

  return (
    <FindReplacePopover
      state={state}
      onClose={onClose}
      onJumpToMatch={handleJumpToMatch}
      onReplaceAll={handleReplaceAll}
      onReplaceOne={handleReplaceOne}
    />
  );
}
